import type { PartnerAccountLink } from "@verity/shared-types";

/**
 * Why a sign-in attempt did not produce a Verity session.
 *
 * ONE named type, widened in place — never inline a second shape at a call site.
 *
 * - `not_authorized` — the BFF answered 401/403: the identity was verified but
 *   is not authorized on this host. Retrying is pointless, so the caller also
 *   evicts the Firebase session. When the BFF additionally reported
 *   `code: "ACCOUNT_MOVED"`, `destination` carries where to go instead.
 * - `network`        — the request never got an answer (fetch rejected, or the
 *   token could not be refreshed). Transient: never evict the session for it.
 * - `unknown`        — any other non-ok status (5xx and friends). Also transient
 *   from the user's point of view; also no eviction.
 */
export type AuthErrorKind = "not_authorized" | "network" | "unknown";

export type AuthError = {
  kind: AuthErrorKind;
  /**
   * Where this account moved to. Only ever set for `not_authorized`, only from
   * the server's `destinations[0]`, and only after passing `safeDestination`
   * below — it is rendered as an anchor href.
   */
  destination?: PartnerAccountLink;
};

/**
 * Accept a server-supplied destination only if it is something we are willing to
 * put in an href.
 *
 * The URL is built by the BFF from a registered partner domain and never from
 * request input, so this is defence in depth rather than the primary control —
 * but it is the last checkpoint before the value becomes a clickable link, and
 * `javascript:` / `data:` URLs are exactly what a link must never carry.
 */
export function safeDestination(value: unknown): PartnerAccountLink | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const { label, url } = value as { label?: unknown; url?: unknown };
  if (typeof label !== "string" || !label.trim()) return undefined;
  if (typeof url !== "string" || !url.trim()) return undefined;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  // The protocol check is the whole guard. A `!parsed.hostname` check next to it
  // would be INERT and was removed after measuring: for a special scheme the
  // WHATWG parser throws rather than yield an empty host (`new URL("https://")`
  // → ERR_INVALID_URL), so no https URL reaching this line can lack one. A guard
  // that cannot fail reads as protection without being any.
  if (parsed.protocol !== "https:") return undefined;

  return { label: label.trim(), url: parsed.toString() };
}

/**
 * Read an `ACCOUNT_MOVED` destination out of a /auth/firebase error body.
 * Returns undefined for every other shape — a body without the code, an empty
 * list, an unparseable payload — so the caller falls back to the generic
 * message rather than to nothing.
 */
export function readMovedDestination(body: unknown): PartnerAccountLink | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const { code, destinations } = body as { code?: unknown; destinations?: unknown };
  if (code !== "ACCOUNT_MOVED") return undefined;
  if (!Array.isArray(destinations)) return undefined;
  return safeDestination(destinations[0]);
}
